import { HTMLMotionProps } from "@enkor/framer-motion";

import { grayScale } from "@enkor/design/colors";
import { Share2Icon } from "@enkor/design/icons";
import { LoadingSpinner } from "@enkor/design/components";
import { iconButtonAnimation } from "@domains/common/utils/animationHelper";
import { getUrlWithUTMParams } from "@logics/utils/makeUTMparameter";
import { removeAmplitudeParams } from "@logics/utils/urlHandler";
import { DEFAULT_TITLE } from "@logics/tags/GeneralHead";
import WebViewBridge from "@logics/utils/webViewBridge";
import { ProjectInfoTrackingDataType } from "@domains/project/types/project";
import shareHandler from "@logics/utils/shareHandler";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { PROJECT_MESSAGE } from "@domains/project/constants/toastMessages";

import * as S from "../ProjectEventDetailButton.style";

interface BaseButtonGroupProps {
  actionButtonProps: HTMLMotionProps<"button">;
  buttonContent?: string;
  projectInfo: ProjectInfoTrackingDataType;
  renderAdditionalButton?: () => React.ReactNode;
}

const BaseButtonGroup = ({
  actionButtonProps,
  buttonContent,
  projectInfo,
  renderAdditionalButton,
}: BaseButtonGroupProps) => {
  const handleClickShare = async () => {
    const url = getUrlWithUTMParams(window.location.toString(), {
      utm_source: "letsfip",
      utm_medium: "share_button",
      utm_campaign: String(projectInfo.projectId),
    });

    const updatedUrl = removeAmplitudeParams(url);

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().triggerShareEvent({
        text: DEFAULT_TITLE,
        subject: projectInfo.projectTitle,
        uri: updatedUrl,
        fileName: DEFAULT_TITLE,
        imageNames: projectInfo.ProjectImageUrlList,
        imagePaths: projectInfo.ProjectImageUrlList,
      });
    } else {
      const shareData = {
        title: "",
        url: updatedUrl,
      };

      const result = await shareHandler(shareData);
      if (result === "shared") {
        dataTrackEvent({
          eventName: DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_SHARE,
          properties: {
            shareType: "system",
            ...projectInfo,
          },
        });

        return;
      }
      if (result === "copiedToClipboard") {
        dataTrackEvent({
          eventName: DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_SHARE,
          properties: { shareType: "clipboard", ...projectInfo },
        });

        ToastSuccess(PROJECT_MESSAGE.SHARE_LINK);

        return;
      }
      if (result === "failed") {
        ToastError(ERROR_MESSAGE.GENERAL);
      }
    }
  };

  return (
    <S.ButtonGroupWrapper>
      <S.ShareButtonBox
        type="button"
        {...iconButtonAnimation}
        onClick={handleClickShare}
      >
        <Share2Icon strokeColor={grayScale.white} size={20} />
      </S.ShareButtonBox>
      <S.FullButton
        type="button"
        {...actionButtonProps}
        {...(!actionButtonProps.disabled && S.ACTIVE_BUTTON_ANIMATION)}
      >
        {buttonContent || (
          <LoadingSpinner loaderType="PulseLoader" color={grayScale.white} />
        )}
      </S.FullButton>
      {renderAdditionalButton && renderAdditionalButton()}
    </S.ButtonGroupWrapper>
  );
};

export default BaseButtonGroup;
